import { EmployeeGradeEnum } from 'common/constants/enum';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import gradeOptions from 'components/elements/table/editable-columns/grade-options';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import { CardContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  BonusPeriodFormType,
  EmployeePerformanceConfigurationFormType,
} from './form-type';
import {
  generateDividenPercentage,
  generatePercentage,
  generatePerformanceByEmployees,
  recalculatePerformanceConfigures,
} from '../hooks/use-get-default-values';

const name = 'employeePerformanceConfigurations' as const;

function useGetPerformanceTableList(): IColumn<EmployeePerformanceConfigurationFormType>[] {
  const { t } = useTranslation();
  return [
    {
      header: t('common:grade'),
      accessorKey: 'grade',
      dataType: ColumnDataType.select,
      options: gradeOptions,
      disabled: true,
      maxSize: 80,
    },
    {
      header: t('common:default_percentage'),
      accessorKey: 'percentage',
      dataType: ColumnDataType.number,
      minNumber: 0,
      maxSize: 150,
    },
    {
      header: t('common:count'),
      accessorKey: 'count',
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
      maxSize: 80,
    },
    {
      header: t('common:percentage'),
      accessorKey: 'dividenPercentage',
      dataType: ColumnDataType.number,
      minNumber: 0,
      maxSize: 80,
    },
  ];
}

export default function PerformanceTableInput() {
  const { editable } = useFormState();
  const { t } = useTranslation();
  const { getValues, clearErrors, control } =
    useFormContext<BonusPeriodFormType>();

  const { fields, replace } = useFieldArray({
    name,
    control,
  });

  const [employees] = useWatch({
    name: ['employees'],
  });

  const columns = useGetPerformanceTableList();

  React.useEffect(() => {
    const employeePerformanceConfigurations = getValues(name);
    const epc = generatePerformanceByEmployees(
      employeePerformanceConfigurations,
      employees,
    );
    replace(epc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const dispatch = React.useCallback(
    (action) => {
      const performances = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const performance = performances[currentRowIndex];
      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof EmployeePerformanceConfigurationFormType;
      const value = action.value;

      const otherVal: () => Partial<EmployeePerformanceConfigurationFormType> =
        () => {
          if (typeof value !== 'number') return {};
          if (columnId === 'dividenPercentage') {
            const percentage = generatePercentage(value, performance.count);
            return {
              percentage,
            };
          } else if (columnId === 'percentage') {
            const dividenPercentage = generateDividenPercentage(
              value,
              performance.count,
            );

            return {
              dividenPercentage,
            };
          }
          return {};
        };

      if (type === 'update_cell') {
        performances[currentRowIndex] = {
          ...performance,
          [columnId]: value,
          ...otherVal(),
        };
        const _performances = recalculatePerformanceConfigures(performances);
        replace(_performances);
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, replace],
  );

  return (
    <CardContainer>
      <Text variant="LabelLarge">{t('common:performance')}</Text>
      <Separator gap={16} />
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
        onCustomDisabledCell={({ rowOriginal }) => {
          return rowOriginal.grade === EmployeeGradeEnum.F;
        }}
        noAddRemoveRow
      />
    </CardContainer>
  );
}
